/**
 * The sidebar. Visual Reference: `_system-sheet.html` §04 — "240px, section
 * labels, active and inactive items".
 *
 * TWO REFERENCES DISAGREE, and the screen wins. `_system-sheet.html` §04 draws
 * the sidebar as a self-contained 240px card with a radius, because a specimen
 * sheet shows a component in isolation — the same presentation device that made
 * `auth-reset.html` look left-aligned (DECISION-039). `dashboard.html` renders
 * the real thing: a full-height column, flush to the page, `border-right` only,
 * no radius and no wordmark above it. That is what a person actually sees.
 *
 * Read off the screen: `--mdx-sidebar-width`, white, `border-right`
 * `--mdx-line`, padding `16px 12px`. Section label 12px / 700 / `.1em` /
 * uppercase / slate, with `8px` beneath it and `20px` above every one after the
 * first. Items stack on `--mdx-space-2`; each is `9px 12px` at radius
 * `--mdx-radius-control` with a `--mdx-icon` glyph on `--mdx-space-10`.
 *
 * Active is `--mdx-brand-tint` behind `--mdx-brand-deep` at weight 600;
 * inactive is `--mdx-ink` at 500. The sheet draws the icon as an 18px square —
 * `DESIGN-SYSTEM.md` flags those as an unfilled slot and ARCH §7.3 fills it
 * with Lucide, so the square becomes the real glyph at the same size.
 *
 * UNDER 1024px the sidebar is not rendered at all; `AppShell` puts the nav in a
 * sheet behind the top bar's menu button instead. ARCH §7.5: two widths, and
 * the switch is a component decision rather than a breakpoint cascade.
 *
 * WHO SEES WHAT is `nav.ts`'s (Story 22.5), and it arrives as a prop rather than
 * through `useShell()`: the 375px sheet renders this outside the shell's provider,
 * and a hook there would throw.
 *
 * Story 3.8.
 */
import { navFor, type NavAccess } from './nav'

export function Sidebar({
  path,
  access,
  onNavigate,
}: {
  path: string
  /** Who is looking — an item the API would refuse them is not offered. */
  access: NavAccess
  onNavigate?: () => void
}) {
  return (
    <nav
      aria-label="Main"
      className="flex w-mdx-sidebar shrink-0 flex-col border-r border-mdx-line bg-mdx-white px-mdx-12 py-mdx-16"
    >
      {navFor(access).map((section, index) => (
        <div key={section.label}>
          <div
            className={`${index === 0 ? '' : 'mt-mdx-20'} mb-mdx-8 ml-mdx-12 text-mdx-12 font-mdx-bold uppercase tracking-mdx-eyebrow text-mdx-slate`}
          >
            {section.label}
          </div>
          <div className="flex flex-col gap-mdx-2">
            {section.items.map((item) => {
              const active = path === item.path
              const Icon = item.icon
              return (
                <a
                  key={item.path}
                  href={item.path}
                  onClick={onNavigate}
                  aria-current={active ? 'page' : undefined}
                  className={[
                    'flex items-center gap-mdx-10 rounded-mdx-control px-mdx-12 py-mdx-9 text-mdx-15 no-underline',
                    'transition-colors duration-mdx-swift ease-mdx-swift',
                    active
                      ? 'bg-mdx-brand-tint font-mdx-semibold text-mdx-brand-deep'
                      : 'font-mdx-medium text-mdx-ink hover:bg-mdx-ground',
                  ].join(' ')}
                >
                  <Icon aria-hidden="true" className="size-mdx-icon shrink-0" strokeWidth={2} />
                  {item.label}
                </a>
              )
            })}
          </div>
        </div>
      ))}
    </nav>
  )
}
